import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { DEFAULT_CLUB_TIMEZONE, type ServerTime } from '@jupi/shared'
import { api } from '../api/client'
import { setClubLocation } from '../clubLocation'
import { getClubTimezone, setClubTimezone } from '../format'

// The server-authoritative clock behind the header display.
//
// The airfield laptop's clock is not to be trusted -- and a booking window
// is decided by the API's clock, not the browser's. So the client syncs
// against GET /api/time, keeps the offset, and ticks locally between syncs
// rather than polling every second. When the two disagree by more than a
// few minutes the display says so, because a member reading a wrong local
// time is how someone turns up an hour late for a slot.
//
// GET /api/time also carries the club's configured timezone, so one poll
// keeps both the clock and every formatted booking time current.

/** How often to re-sync with the server. */
const RESYNC_MS = 5 * 60 * 1000

/** Below this, the device clock is close enough to say nothing about. */
const SKEW_THRESHOLD_MS = 5 * 60 * 1000

export interface ServerClockState {
  /** UTC epoch in ms, corrected for the device's offset from the server. */
  utcMs: number
  /** The club's configured timezone. */
  timezone: string
  /** Whether the first sync has landed. */
  synced: boolean
  /** True when the device clock is more than SKEW_THRESHOLD_MS out. */
  skewed: boolean
  /** Offset in ms: positive means the device clock is behind the server. */
  skewMs: number
  /**
   * Wrangler's APP_ENV (development/beta/production). Defaults to
   * 'production' before the first sync so the env badge stays hidden
   * rather than flashing on in production while /api/time is in flight.
   */
  appEnv: string
  /** Semver build version from GET /api/time ('dev' locally / before first sync). */
  appVersion: string
  /**
   * Force a sync now. The admin settings page calls it after changing the
   * club timezone, so the header clock follows the save instead of waiting
   * out the five-minute poll.
   */
  resync: () => void
}

// Built on demand rather than held as a constant: as a constant its `utcMs`
// would be frozen at the moment this module was first imported, so any
// consumer outside a provider would report import time instead of now.
const unsynced = (): ServerClockState => ({
  utcMs: Date.now(),
  timezone: getClubTimezone(),
  synced: false,
  skewed: false,
  skewMs: 0,
  appEnv: 'production',
  appVersion: 'dev',
  resync: () => {},
})

// `null` rather than a default object, so useServerClock can tell "no
// provider above me" apart from "a provider that has not synced yet".
const ServerClockContext = createContext<ServerClockState | null>(null)

export function ServerClockProvider({ children }: { children: ReactNode }) {
  // The offset lives in a ref so the one-second tick always reads the
  // latest value without having to tear down and rebuild its interval.
  const offsetRef = useRef(0)
  const [state, setState] = useState<ServerClockState>(unsynced)
  // Held in a ref so `resync` is stable: it goes into state, and a new
  // function identity every render would make the context value change on
  // every tick for consumers comparing it.
  const syncRef = useRef<() => void>(() => {})

  const sync = useCallback(async () => {
    try {
      const before = Date.now()
      const data = await api.get<ServerTime>('/api/time')
      const after = Date.now()

      // NTP-style delay compensation, assuming symmetric latency: compare
      // the server's instant against the midpoint of the round trip rather
      // than against either end of it.
      const offset = data.epochMs - (before + after) / 2
      offsetRef.current = offset
      setClubTimezone(data.timezone)
      setClubLocation(data.homeBase)

      setState((prev) => ({
        ...prev,
        utcMs: Date.now() + offset,
        timezone: data.timezone || DEFAULT_CLUB_TIMEZONE,
        synced: true,
        skewed: Math.abs(offset) > SKEW_THRESHOLD_MS,
        skewMs: offset,
        appEnv: data.appEnv || 'production',
        appVersion: data.appVersion || 'dev',
      }))
    } catch {
      // Offline or 500: keep the offset we already have. A stale offset is
      // far better than falling back to an unsynced device clock.
    }
  }, [])

  useEffect(() => {
    const tick = setInterval(() => {
      setState((prev) => ({ ...prev, utcMs: Date.now() + offsetRef.current }))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  useEffect(() => {
    syncRef.current = () => void sync()
    setState((prev) => ({ ...prev, resync: () => syncRef.current() }))
    void sync()
    const timer = setInterval(() => void sync(), RESYNC_MS)
    return () => clearInterval(timer)
  }, [sync])

  return <ServerClockContext.Provider value={state}>{children}</ServerClockContext.Provider>
}

/**
 * The server clock. Outside a `<ServerClockProvider>` it degrades to the
 * device clock, read afresh on each render -- it cannot tick on its own,
 * but it is never more than one render stale.
 */
export const useServerClock = (): ServerClockState => useContext(ServerClockContext) ?? unsynced()
